import React, { useEffect, useState } from 'react';
import { Loader2, ChevronDown, Bike, Clock, MapPin, Wrench, Target } from 'lucide-react';
import PageShell, { SectionTitle } from './PageShell';
import { api } from '../lib/api';

const PHASE_ORDER = [
  ['evalStatique', '1 · Évaluation statique', '🧍'],
  ['evalDynamique', '2 · Évaluation dynamique', '🏍'],
  ['objectif', '3 · Objectif annoncé', '🎯'],
  ['criteres', '4 · Critères de réussite', '✅'],
  ['cours', '5 · Cours (théorie)', '📖'],
  ['demonstration', '6 · Démonstration', '👀'],
  ['guidage', '7 · Guidage', '🤝'],
  ['autonomie', '8 · Autonomie', '🚀'],
  ['bilan', '9 · Bilan', '💬'],
];

function FicheCard({ fiche }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-sky-500/25 bg-[#0b0f1a] overflow-hidden" data-testid={`fiche-plateau-${fiche.slug}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 p-4 sm:p-5 text-left" data-testid={`fiche-plateau-toggle-${fiche.slug}`}>
        <span className="w-11 h-11 rounded-xl bg-sky-500/15 border border-sky-500/40 text-sky-300 flex items-center justify-center shrink-0"><Bike size={18} /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold">{fiche.competence} · {fiche.competenceLabel || fiche.kicker}</span>
          <span className="block text-white font-serif-editorial text-[18px] sm:text-[20px] leading-snug mt-0.5">{fiche.title}</span>
          <span className="block text-white/50 text-[12px] mt-1 line-clamp-2">{fiche.summary}</span>
        </span>
        <ChevronDown size={17} className={`text-white/40 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-5">
          {fiche.meta && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
              {[[Clock, fiche.meta.duree], [Target, fiche.meta.niveau], [MapPin, fiche.meta.lieu], [Wrench, fiche.meta.materiel]].filter(([, v]) => v).map(([Icon, v], i) => (
                <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5 flex items-start gap-2">
                  <Icon size={13} className="text-sky-400 shrink-0 mt-0.5" />
                  <span className="text-white/70 text-[11.5px] leading-snug">{v}</span>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-3">
            {PHASE_ORDER.map(([key, label, emoji]) => {
              const step = fiche.steps?.[key];
              if (!step) return null;
              return (
                <div key={key} className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
                  <div className="text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold mb-1.5">{emoji} {label}</div>
                  {step.content && <div className="text-white/80 text-[13px] leading-relaxed article-html" dangerouslySetInnerHTML={{ __html: step.content }} />}
                  {(step.items || []).length > 0 && (
                    <ul className="mt-2 space-y-1.5">
                      {step.items.map((it, i) => (
                        <li key={i} className="flex gap-2 text-[12.5px] text-white/65 leading-snug">
                          <span className="text-sky-400 shrink-0">▸</span>
                          <span className="article-html" dangerouslySetInnerHTML={{ __html: it }} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TeacherFichesPlateauPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = 'Fiches pédagogiques plateau moto | Espace enseignant';
    api.motoFichesPlateau().then(setData).catch(() => setData({ fiches: [] }));
  }, []);

  return (
    <PageShell>
      <section className="max-w-4xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12" data-testid="teacher-fiches-plateau-page">
        <SectionTitle
          kicker="Espace enseignant · Moto"
          title="Fiches pédagogiques du plateau"
          sub="Le déroulé complet de chaque séance plateau : évaluation, objectif, cours, démonstration, guidage, autonomie et bilan — prêt à dérouler sur la piste."
        />
        {!data ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-sky-400" size={28} /></div>
        ) : (
          <div className="mt-8 space-y-3 mb-6">
            {(data.fiches || []).map((f) => <FicheCard key={f.slug} fiche={f} />)}
          </div>
        )}
      </section>
    </PageShell>
  );
}
